/**
 * Plans & séances — §C. Liste les plans d'entraînement (périodisation, volume
 * cible) et, à plat, les séances à venir. Gestion manuelle complète : créer une
 * séance à la main, l'ouvrir pour gérer finement ses exercices (éditeur), la
 * marquer « réalisée » (RPE / durée) ou la supprimer. La complétion alimente la
 * compliance et la charge du dashboard.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  Pill,
  SESSION_TYPE_LABELS,
  SectionTitle,
  cardSx,
  darkFieldSx,
} from './shared'
import SessionDetailView from './SessionDetailView'

const PHASE_LABELS = {
  base: 'Base',
  progression: 'Progression',
  intensification: 'Intensification',
  recovery: 'Deload',
}

const STATUS_COLOR = {
  planned: '#5b8cff',
  completed: '#3ddc97',
  partial: PERF_ACCENT,
  skipped: '#ff4d4f',
  moved: 'rgba(255,255,255,0.5)',
}

const STATUS_LABELS = {
  planned: 'Prévue',
  completed: 'Réalisée',
  partial: 'Partielle',
  skipped: 'Manquée',
  moved: 'Déplacée',
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const NEW_SESSION = () => ({
  sport: '',
  title: '',
  session_type: 'strength',
  scheduled_date: todayISO(),
  planned_duration_min: 60,
})

export default function PlansView() {
  const [plans, setPlans] = useState([])
  const [sessions, setSessions] = useState([])
  const [prefs, setPrefs] = useState([])
  const [loading, setLoading] = useState(true)
  const [complete, setComplete] = useState(null) // session
  const [duration, setDuration] = useState('')
  const [rpe, setRpe] = useState('')
  const [busy, setBusy] = useState(false)
  const [detail, setDetail] = useState(null) // séance ouverte dans l'éditeur
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(NEW_SESSION())

  const load = async () => {
    setLoading(true)
    const [p, s, pr] = await Promise.all([
      performanceApi.plans.list(),
      performanceApi.sessions.list('?status=planned'),
      performanceApi.preferences.list(),
    ])
    setPlans(p)
    setSessions(s)
    setPrefs(pr)
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const markDone = async () => {
    if (!complete) return
    setBusy(true)
    try {
      await performanceApi.completeSession(complete.id, {
        actual_duration_min: duration || null,
        perceived_effort: rpe || null,
      })
      setComplete(null)
      setDuration('')
      setRpe('')
      await load()
    } finally {
      setBusy(false)
    }
  }

  const createSession = async () => {
    if (!form.sport || !form.scheduled_date) return
    setBusy(true)
    try {
      const pref = prefs.find((p) => p.sport === form.sport)
      const created = await performanceApi.sessions.create({
        sport: form.sport,
        title:
          form.title.trim() ||
          `${pref ? pref.sport_name : 'Séance'} — ${SESSION_TYPE_LABELS[form.session_type]}`,
        session_type: form.session_type,
        status: 'planned',
        scheduled_date: form.scheduled_date,
        planned_duration_min: form.planned_duration_min || null,
      })
      setCreating(false)
      setForm(NEW_SESSION())
      await load()
      // Ouvre directement l'éditeur d'exercices de la nouvelle séance.
      if (created && created.id) setDetail(created)
    } finally {
      setBusy(false)
    }
  }

  const deleteSession = async (s) => {
    setBusy(true)
    try {
      await performanceApi.sessions.remove(s.id)
      await load()
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <Loading label="Chargement de vos plans…" />

  return (
    <Box>
      <SectionTitle>Plans d'entraînement</SectionTitle>
      {plans.length === 0 ? (
        <Box sx={{ ...cardSx, mb: 4 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucun plan actif. Générez votre semaine depuis l'onglet Planning, ou
            créez vos séances à la main ci-dessous : chaque séance est ensuite
            éditable exercice par exercice.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {plans.map((p) => (
            <Grid item xs={12} md={6} key={p.id}>
              <Box sx={{ ...cardSx, height: '100%' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 1 }}
                >
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                    {p.name}
                  </Typography>
                  <Pill label={PHASE_LABELS[p.phase] || p.phase} />
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {p.sport_name && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      {p.sport_name}
                    </Typography>
                  )}
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                    {p.sessions_per_week} séances/sem.
                  </Typography>
                  {p.weekly_volume_target && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                      Volume {p.weekly_volume_target} {p.volume_unit}
                    </Typography>
                  )}
                  <Typography sx={{ color: PERF_ACCENT, fontSize: 13 }}>
                    {p.session_count} séance{p.session_count > 1 ? 's' : ''}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <SectionTitle>Prochaines séances</SectionTitle>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => { setForm(NEW_SESSION()); setCreating(true) }}
          sx={{ color: PERF_ACCENT, textTransform: 'none' }}
        >
          Nouvelle séance
        </Button>
      </Stack>

      {sessions.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucune séance prévue. Créez-en une à la main, ou générez votre
            planning depuis l'onglet Planning.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {sessions.map((s) => {
            const nbEx = (s.exercises || []).length
            return (
              <Box
                key={s.id}
                onClick={() => setDetail(s)}
                sx={{
                  ...cardSx,
                  cursor: 'pointer',
                  borderLeft: `4px solid ${STATUS_COLOR[s.status] || PERF_ACCENT}`,
                  '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                      {s.title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                      {s.scheduled_date}
                      {s.scheduled_time ? ` · ${s.scheduled_time.slice(0, 5)}` : ''}
                      {' · '}
                      {SESSION_TYPE_LABELS[s.session_type] || s.session_type}
                      {s.sport_name ? ` · ${s.sport_name}` : ''}
                      {s.planned_load ? ` · charge ${s.planned_load}/10` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Pill
                      label={`${nbEx} exo${nbEx > 1 ? 's' : ''}`}
                      color={nbEx ? '#3ddc97' : 'rgba(255,255,255,0.4)'}
                    />
                    <Pill
                      label={STATUS_LABELS[s.status] || s.status}
                      color={STATUS_COLOR[s.status] || PERF_ACCENT}
                    />
                    <Button
                      size="small"
                      startIcon={<FitnessCenterRoundedIcon />}
                      onClick={(e) => { e.stopPropagation(); setDetail(s) }}
                      sx={{ color: PERF_ACCENT, textTransform: 'none' }}
                    >
                      Exercices
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CheckCircleRoundedIcon />}
                      onClick={(e) => {
                        e.stopPropagation()
                        setComplete(s)
                        setDuration(s.planned_duration_min || '')
                        setRpe('')
                      }}
                      sx={{ color: '#3ddc97', textTransform: 'none' }}
                    >
                      Réalisée
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); deleteSession(s) }}
                      disabled={busy}
                      sx={{ color: '#ff6b6b' }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      )}

      {/* Éditeur d'exercices */}
      <SessionDetailView
        session={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onChanged={load}
      />

      {/* Complétion d'une séance */}
      <Dialog open={!!complete} onClose={() => setComplete(null)}
        PaperProps={{ sx: { bgcolor: '#11162a', color: '#fff' } }}>
        <DialogTitle>Séance réalisée — {complete?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              type="number"
              label="Durée réelle (min)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              fullWidth
              sx={darkFieldSx}
            />
            <TextField
              type="number"
              label="Effort ressenti (RPE 1-10)"
              value={rpe}
              onChange={(e) => setRpe(e.target.value)}
              inputProps={{ min: 1, max: 10 }}
              fullWidth
              sx={darkFieldSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplete(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Annuler
          </Button>
          <Button
            onClick={markDone}
            disabled={busy}
            variant="contained"
            sx={{ bgcolor: '#3ddc97', color: '#0b0f1f' }}
          >
            Valider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Création manuelle d'une séance */}
      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#11162a', color: '#fff' } }}>
        <DialogTitle>Nouvelle séance</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Sport"
              value={form.sport}
              onChange={(e) => setForm((f) => ({ ...f, sport: e.target.value }))}
              fullWidth
              sx={darkFieldSx}
            >
              {prefs.length === 0 && (
                <MenuItem value="" disabled>
                  Aucun sport — ajoutez-en dans Réglages
                </MenuItem>
              )}
              {prefs.map((p) => (
                <MenuItem key={p.id} value={p.sport}>
                  {p.sport_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Titre (optionnel)"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Laisser vide pour un titre automatique"
              fullWidth
              sx={darkFieldSx}
            />
            <TextField
              select
              label="Type de séance"
              value={form.session_type}
              onChange={(e) => setForm((f) => ({ ...f, session_type: e.target.value }))}
              fullWidth
              sx={darkFieldSx}
            >
              {Object.entries(SESSION_TYPE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label="Date"
                value={form.scheduled_date}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={darkFieldSx}
              />
              <TextField
                type="number"
                label="Durée (min)"
                value={form.planned_duration_min}
                onChange={(e) => setForm((f) => ({ ...f, planned_duration_min: e.target.value }))}
                fullWidth
                sx={darkFieldSx}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Annuler
          </Button>
          <Button
            onClick={createSession}
            disabled={busy || !form.sport}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Créer et éditer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
